import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ShieldCheck, LayoutDashboard, ListChecks, Grid3X3, History, Bell, ClipboardList, Archive } from "lucide-react";
import { QmsChecklists } from "@/components/qms/QmsChecklists";
import api from "@/lib/api";
import { QmsRegister } from "@/components/qms/QmsRegister";
import { QmsDashboard } from "@/components/qms/QmsDashboard";
import { QmsMatrix } from "@/components/qms/QmsMatrix";
import { QmsAudit } from "@/components/qms/QmsAudit";

const PHASE_KEYS = ["1", "2", "3", "4", "5"];

export default function Qms() {
  const [view, setView] = useState("dashboard");
  const meta = useQuery({ queryKey: ["qms", "meta"], queryFn: async () => (await api.get("/qms/meta")).data });
  const m = meta.data;
  const reg = m?.registers.find((r) => r.key === view);
  const NavBtn = ({ k, label, icon: Icon, count }) => (
    <button onClick={() => setView(k)} className={`w-full text-left flex items-center gap-2 rounded px-2.5 py-1.5 text-xs transition-colors ${view === k ? "bg-slate-900 text-white" : "hover:bg-slate-100 text-slate-700"}`} data-testid={`qms-nav-${k}`}>
      {Icon && <Icon size={13} />}<span className="flex-1 truncate">{label}</span>{count != null && <span className={`font-mono text-[10px] ${view === k ? "text-slate-300" : "text-slate-400"}`}>{count}</span>}
    </button>
  );
  return (
    <div className="space-y-4" data-testid="qms-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><div className="pp-label text-blue-400">Quality assurance</div><h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight flex items-center gap-2"><ShieldCheck size={26} /> Quality Management System</h1><p className="text-sm text-slate-500">ISO 9001 registers · approval workflow · audit trail · clause matrix</p></div>
      </div>
      <div className="grid lg:grid-cols-[250px_1fr] gap-4 items-start">
        <aside className="pp-card p-2 space-y-2 lg:sticky lg:top-4" data-testid="qms-nav">
          <NavBtn k="dashboard" label="QMS Dashboard" icon={LayoutDashboard} />
          <NavBtn k="matrix" label="Clause matrix & Gap assessment" icon={Grid3X3} />
          <NavBtn k="checklists" label="Auditor checklists" icon={ClipboardList} />
          <NavBtn k="alerts" label="Alerts due" icon={Bell} />
          <NavBtn k="audit" label="Audit trail" icon={History} />
          <NavBtn k="obsolete" label="Obsolete Documents" icon={Archive} count={m?.obsolete_docs} />
          {m && PHASE_KEYS.map((p) => <div key={p}><div className="pp-label px-2.5 pt-2 pb-1 text-[10px]">{m.phases[p]}</div>{m.registers.filter((r) => String(r.phase) === p).map((r) => <NavBtn key={r.key} k={r.key} label={`${r.label} · ${r.clause}`} icon={ListChecks} count={r.count} />)}</div>)}
        </aside>
        <main className="min-w-0">
          {view === "dashboard" && <QmsDashboard onOpen={setView} canEdit={m?.can_edit} />}
          {view === "matrix" && m && <QmsMatrix meta={m} onOpen={setView} />}
          {view === "checklists" && m && <QmsChecklists meta={m} />}
          {view === "audit" && <QmsAudit />}
          {view === "alerts" && <QmsAudit alerts />}
          {view === "obsolete" && m && <QmsRegister key="obsolete" reg={m.registers.find((r) => r.key === "documents")} meta={m} fixedStatus="Obsolete" />}
          {reg && m && <QmsRegister key={reg.key} reg={reg} meta={m} />}
        </main>
      </div>
    </div>
  );
}
